import {
    DataGridPro,
    useGridApiRef,
    type GridSortModel
} from '@mui/x-data-grid-pro';
import { getWorkflowprotocolCols } from '../utils/WorkflowDataGridConfig';
import { Box, CircularProgress, Button, Typography, Grid2, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from "../contexts/GlobalState"
import { useState, useEffect, useCallback, useMemo } from "react";
import { getWorkflowData, useGetWorkflowProtocols } from '../api/vegApl';
import { Providers, SimpleProvider, ProviderState } from "@microsoft/mgt-element";
import GenericFilterPanel from '../components/common/GenericFilterPanel';
import { useCommonStyles } from '../styles/GenericStyle';
import { getDataGridStyles } from '../styles/MtComStyleObj';
import { useResponsiveHeaderHeight } from '../hooks/useResponsiveHeaderHeight';
import ShowFilterChips from "../components/common/ShowFilterChips";
import FilterButton from '../components/common/FilterButton';
import { addIdIncrementWithAddCropSite } from '../utils/HelperFunction';
import type { ListValueWorkFlow } from '../model/commonType';
import { usePersistedGridState } from '../hooks/usePersistedGridState';
import useGetSortedRefCrops from '../api/useGetSortedRefCrops';

type WorkflowProtocol = {
    workflow_protocol_id: string | number;
    protocol_title: string;
    protocol_crop: string;
    protocol_site: string;
    protocol_year: string | number;
    protocol_steps: string[];
    protocol_status: string;
    // add other fields if needed
};

const normalizeCropKey = (value: unknown): string =>
    typeof value === 'string' || typeof value === 'number'
        ? String(value).trim().toLowerCase()
        : '';

const filterRowsBySiteCrops = (rows: WorkflowProtocol[], siteCrops: any[], siteSelected?: string): WorkflowProtocol[] => {
    if (!Array.isArray(rows)) return [];
    if (!siteSelected) return rows;
    if (!Array.isArray(siteCrops) || siteCrops.length === 0) return [];

    const allowedCropKeys = new Set(
        siteCrops
            .flatMap((crop: any) => [crop?.code, crop?.name, crop?.crop_xrd_oid])
            .map(normalizeCropKey)
            .filter(Boolean)
    );

    return rows.filter((row: any) => {
        const rowCropKeys = [row?.protocol_crop, row?.crop, row?.crop_code, row?.cropName]
            .map(normalizeCropKey)
            .filter(Boolean);
        return rowCropKeys.some((key) => allowedCropKeys.has(key));
    });
};

const getListValuesFromGridData = (gridData: WorkflowProtocol[], crops: any[], sites: any[]) => {
    // Extract steps and statuses from protocol_steps and protocol_status
    const stepsSet = new Set();
    const statusesSet = new Set();
    const workflowNamesSet = new Set();
    gridData.forEach(item => {
        // protocol_steps is an array
        if (Array.isArray(item.protocol_steps)) {
            item.protocol_steps.forEach(step => stepsSet.add(step));
        }
        // protocol_status is a string
        // if (item.protocol_status) {
        //     statusesSet.add(   item.protocol_status);
        // }
        // protocol_title is a string
        if (item.protocol_title) {
            workflowNamesSet.add(item.protocol_title);
        }
    });
    return {
        workflowNames: Array.from(workflowNamesSet)
            .filter(Boolean)
            .map(name => ({ name })),
        crops: crops,
        sites: sites.map(item => ({
            ...item,
            site_name: [item?.site_name, item?.full_code].filter(Boolean).join('-')
        })),
        // years: Array.from(new Set(gridData.map(item => item.protocol_year)))
        //     .filter(Boolean)
        //     .map(year => ({ year })),     
        statuses: Array.from(statusesSet)
            .filter(Boolean)
            .map(status => ({ id: status, name: String(status) })),
    };
};

export const WorkflowPage = () => {
    const navigate = useNavigate();
    const [mode, _setMode] = useState('view');
    const { sites, state } = useGlobalState();
    const { data: siteCrops = [] } = useGetSortedRefCrops(state?.siteSelected);
    const [userGridData, setUsersGridData] = useState<WorkflowProtocol[]>([]);
    const [originalGridData, setOriginalGridData] = useState<WorkflowProtocol[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const apiRef = useGridApiRef();
    const { applyColumnWidths, onColumnWidthChange } = usePersistedGridState('workflow-grid');
    const [isFilterVisible, setFilterVisible] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'protocol_year', sort: 'asc' }]);
    const [filterValues, setFilterValues] = useState<any[]>([]);
    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    }, []);
    const [filterDel, setFilterDel] = useState<Record<string, any>>({});
    Providers.globalProvider = new SimpleProvider((_scopes) => {
        let accessToken = state?.accessToken;
        return new Promise((resolve) => {
            resolve(accessToken);
        });
    });
    const [listValues, setListValue] = useState<ListValueWorkFlow>({
        workflowNames: [],
        sites: [],
        crops: [],
        years: [{ year: 2024 }, { year: 2025 }, { year: 2026 }],
        steps: [],
        statuses: [{id:'active',name:"Active"},{id:'inactive',name:"Inactive"}]

    });
    Providers.globalProvider.setState(ProviderState.SignedIn);

    const { mutate: getAllUsersListCall } = getWorkflowData({
        mutation: {
            onSuccess: (data) => {
                setIsLoading(false);
                const workflowData = data?.data ?? [];
                setOriginalGridData([...workflowData]);
            },
            onError: () => {
                setIsLoading(false);
            }
        },
    })


    const {
        data: workFlowProtocols = [],
    } = useGetWorkflowProtocols({
        query: {
            queryKey: ["workProtocols"],
            select: (d) => {
                let data = d?.data ?? [];                        
                return data;
            },
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchOnMount: false,


        },
    })

    useEffect(() => {
        const roleName = state.roleSelected || state.role?.[0]?.role_name;
        const hasAccess = roleName === "S2S-Lead" || roleName === "Admin";
        setHasAccess(hasAccess);
    }, [state.role, state.roleSelected]);

    const filteredWorkflowRows = useMemo(
        () => filterRowsBySiteCrops(originalGridData, siteCrops, state?.siteSelected),
        [originalGridData, siteCrops, state?.siteSelected]
    );

    useEffect(() => {
        
        setIsLoading(true);
        getAllUsersListCall({});
    }, []);

    useEffect(() => {

       let listValues = getListValuesFromGridData(filteredWorkflowRows, siteCrops, sites);     
        setListValue((prev) => ({
            ...prev, "crops": listValues?.crops ?? [],
            "sites": listValues?.sites ?? [],
            // "statuses": listValues?.statuses ?? [],
            "workflowNames": listValues?.workflowNames ?? [],  
            "steps": workFlowProtocols ?? [],          
        }));



    }, [siteCrops, workFlowProtocols, sites, filteredWorkflowRows]);




    const handleSetGridData = (data: any[]) => {
        const siteFilteredRows = filterRowsBySiteCrops(data as WorkflowProtocol[], siteCrops, state?.siteSelected);
        const cropMap = siteCrops.reduce((acc: any, item: any) => {
            acc[item?.code] = item?.name;
            return acc;
        }, {});

        const siteMap = sites.reduce((acc: any, item: any) => {
            acc[item?.full_code] = item?.site_name + "-" + item?.full_code;
            return acc;
        }, {});

        const mappedData = addIdIncrementWithAddCropSite(siteFilteredRows, cropMap, siteMap);
        setUsersGridData([...mappedData]);
    };

    const classes = useCommonStyles();
    const headerHeight = useResponsiveHeaderHeight(48);
    const hasAppliedFilters = Object.keys(filterValues ?? {}).length > 0;

    const handleDeleteFilter = useCallback((filter: any) => {

        setFilterDel(filter);
    }, []);



    const getCropSiteMap = useCallback(() => {
        const crop = siteCrops.reduce((acc: any, item: any) => {
            acc[item?.code] = item?.name;
            return acc;
        }, {});

        const site = sites.reduce((acc: any, item: any) => {
            acc[item?.full_code] = item?.site_name + "-" + item?.full_code;
            return acc;
        }, {});
        return {
            crop, site
        }

    }, [siteCrops, sites]);


    useEffect(() => {
        let listVal = getCropSiteMap();
        let userData = addIdIncrementWithAddCropSite(filteredWorkflowRows, listVal?.crop, listVal?.site);

        setUsersGridData([...userData]);
    }, [siteCrops, sites, filteredWorkflowRows]);




    return (
        <div data-testid="WorkflowPage">
            <Grid2 size={{ xs: 12, sm: 12, md: 12 }} sx={{ mt: 1, px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }} onClick={() => navigate(-1)}>
                    <ArrowBackIcon sx={{ fontSize: 20, color: '#000000' }} />
                    <Typography className={classes.labelNoto20Bold600}>Back</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography className={classes.labelNoto36Bold700} sx={{ color: '#1E1E1E' }}>
                        Configure Workflow
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: { xs: '100%', sm: 'auto' },
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                    }}>
                        <FilterButton isFilterVisible={isFilterVisible} setFilterVisible={setFilterVisible} />

                        {hasAccess && <Tooltip title="Action not ready. Under development." arrow placement="top">
                            <span>
                                <Button
                                    variant="contained"
                                    disableRipple
                                    disabled
                                    startIcon={<AddIcon />}
                                    className={classes.greenButton}
                                    sx={{
                                        minWidth: { xs: 0, sm: 200 },
                                        width: { xs: '100%', sm: 'auto' },
                                        height: 44,
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: { xs: 14, sm: 16 }
                                    }}>
                                    Create New Workflow
                                </Button>
                            </span>
                        </Tooltip>}
                    </Box>
                </Box>

                {hasAppliedFilters && <ShowFilterChips filterValues={filterValues} onDelete={handleDeleteFilter} />}

                {mode == "view" && (

                    isLoading ? (
                        <Box sx={{ display: 'flex', placeItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <Box sx={{ width: '100%', mt: hasAppliedFilters ? 0 : '22px' }}>
                            <DataGridPro
                                rows={userGridData ? userGridData : []}
                                columns={applyColumnWidths(getWorkflowprotocolCols(classes, hasAccess, sortModel))}
                                pageSizeOptions={[20, 50, 100, 500]}
                                columnHeaderHeight={headerHeight}
                                getRowHeight={() => "auto"}
                                disableRowSelectionOnClick
                                getRowId={(r) => r.workflow_protocal_id}
                                pagination
                                apiRef={apiRef}
                                sortModel={sortModel}
                                onSortModelChange={handleSortModelChange}
                                onColumnWidthChange={onColumnWidthChange}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 50, page: 0 } },
                                }}
                                localeText={{
                                    noRowsLabel: 'No Workflows found',
                                    noResultsOverlayLabel: 'No Workflows found',
                                }}
                                sx={(theme) => getDataGridStyles(theme, {
                                    headerColor: '#DEDEDE',
                                    fillerColor: '#DEDEDE',
                                    maxHeight: 'calc(100vh - 310px)',
                                })}
                            />
                        </Box>
                    )

                )}
            </Grid2>

            <>
                <Box sx={{ width: "100%" }}><GenericFilterPanel moduleName='workflow' listValues={listValues} setGridData={handleSetGridData} onRefreshData={() => getAllUsersListCall({})} isFilterVisible={isFilterVisible} setFilterVisible={setFilterVisible} setFilterFieldData={setFilterValues} filtetToDelete={filterDel} /></Box>
            </>
        </div >

    );
}
export default WorkflowPage;
